import { useEffect, useRef } from 'react';
import { FOCUSABLE, nextTrapIndex } from '../utils/focusTrap';

/**
 * Modal dialog focus management: focus moves in on open, Tab stays inside,
 * Escape calls onClose, and focus returns to the opener on close. Attach the
 * returned ref to the element with role="dialog".
 */
export function useDialogFocus({ open, onClose }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const focusables = () => [...dialog.querySelectorAll(FOCUSABLE)];
    (focusables()[0] ?? dialog).focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      event.preventDefault();
      const items = focusables();
      const target = nextTrapIndex(items.length, items.indexOf(document.activeElement), event.shiftKey);
      (target === -1 ? dialog : items[target]).focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [open]);

  return dialogRef;
}
